import { FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ADMIN_PERMISSIONS,
  ALL_PERMISSIONS,
  PERMISSION_GROUPS,
  Permission,
  UserStatus,
  canEditRole,
  hasPermission,
  impliedBy,
  normalizePermissions,
  type PermissionKey,
} from '@construction/shared';
import { rolesApi, usersApi, type Role } from '@/api/endpoints';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Drawer,
  EmptyState,
  Field,
  Input,
  ListGroup,
  ListRow,
  PageHeader,
  Spinner,
  Tag,
} from '@/components/ui';
import { OrgSelect, type OrgChoice } from '@/features/orgs';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { useMobileShell } from '@/hooks/useMobileShell';
import { cn } from '@/lib/cn';
import {
  PERMISSION_GROUP_LABEL_KEY,
  PERMISSION_HINT_KEY,
  PERMISSION_LABEL_KEY,
  usePermissions,
} from '@/lib/permissions';

/**
 * 职位与权限（CR-038；有 `role.manage` 的人进得来，CR-073 之前只有超管）。CR-082 按示例重做：
 *
 * **左边职位列表，右边这个职位的权限表**（按模块一行一组勾，每个勾下面一句它意味着什么），改完点保存。
 * 改之前是一行一个职位、点开看权限、再点「编辑」弹抽屉 —— 三下才看得到勾，客户说不像后台。
 * 手机上是列表，点一个从底部弹出同一张表。
 *
 * - 系统职位（超管）显示全勾但灰掉 —— 客户原话「超级管理员权限无法被修改」；
 * - 单位（CR-073）：共享职位各家能用不能改，只有超管能动；各家能建「比自己小」的 ——
 *   每一行能不能动、每个勾能不能勾都问 shared 的 `canEditRole`，和服务端同一份。动不了的职位右边是只读的表；
 * - 删职位前要没人挂在上面：服务端返回 ROLE_IN_USE 带人数，这里原样显示。
 */
export function RolesPage() {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();
  const isMobileShell = useMobileShell();
  // 选中的职位；'new' = 新建。电脑上没选就默认第一个，手机上没选就是列表
  const [selectedId, setSelectedId] = useState<number | 'new' | null>(null);

  const roles = useQuery({ queryKey: ['roles'], queryFn: () => rolesApi.list() });
  const list = roles.data ?? [];
  const selected =
    selectedId === 'new'
      ? null
      : (list.find((r) => r.id === selectedId) ?? (isMobileShell ? null : (list[0] ?? null)));
  const creating = selectedId === 'new';

  const detail = (creating || selected) && (
    <RoleDetail
      // 换职位就重挂：表单里的勾从这个职位重新开始
      key={creating ? 'new' : selected!.id}
      role={creating ? undefined : selected!}
      onCreated={(role) => setSelectedId(role.id)}
      onDeleted={() => setSelectedId(null)}
      onCancelNew={() => setSelectedId(null)}
      bare={isMobileShell}
    />
  );

  return (
    <>
      <PageHeader
        title={t('roles.title')}
        description={t('roles.description')}
        action={
          <Button onClick={() => setSelectedId('new')} data-action="roles.create">
            {t('roles.create')}
          </Button>
        }
      />

      {roles.isLoading && (
        <div className="flex justify-center py-16 text-ink-3">
          <Spinner />
        </div>
      )}
      {roles.isError && <Alert>{toMessage(roles.error)}</Alert>}
      {roles.data && list.length === 0 && !creating && <EmptyState title={t('roles.empty')} />}

      {list.length > 0 &&
        (isMobileShell ? (
          <>
            <ListGroup>
              {list.map((role) => (
                <ListRow
                  key={role.id}
                  onClick={() => setSelectedId(role.id)}
                  title={<RoleTitle role={role} />}
                  subtitle={role.description || undefined}
                  trailing={<RoleCounts role={role} />}
                />
              ))}
            </ListGroup>
            <Drawer
              open={selectedId !== null}
              onClose={() => setSelectedId(null)}
              title={creating ? t('roles.create') : (selected?.name ?? '')}
              subtitle={selected && !creating && <RoleSummary role={selected} />}
            >
              {detail}
            </Drawer>
          </>
        ) : (
          <div className="grid items-start gap-4 lg:grid-cols-[17rem_minmax(0,1fr)]">
            <Card className="overflow-hidden">
              <ul>
                {list.map((role) => {
                  const on = !creating && selected?.id === role.id;
                  return (
                    <li key={role.id} className="border-b border-rule last:border-0">
                      <button
                        type="button"
                        onClick={() => setSelectedId(role.id)}
                        aria-current={on}
                        className={cn(
                          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
                          on ? 'bg-brand-50' : 'hover:bg-surface-2/60',
                        )}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block text-body font-semibold text-ink">
                            <RoleTitle role={role} />
                          </span>
                          {role.description && (
                            <span className="mt-0.5 line-clamp-2 block text-caption text-ink-3">{role.description}</span>
                          )}
                        </span>
                        <span className="shrink-0 text-caption tabular-nums text-ink-3">
                          {t('roles.userCount', { count: role._count.users })}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Card>
            {detail}
          </div>
        ))}

      {/* 一个职位都没有的时候也能新建 */}
      {list.length === 0 && creating && detail}
    </>
  );
}

/** 名字 + 系统 / 共享 / 哪家的标签 */
function RoleTitle({ role }: { role: Role }) {
  const { t } = useTranslation();
  const { isPlatform } = usePermissions();
  const shared = !role.isSystem && role.orgId === null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {role.name}
      {role.isSystem && <Tag tone="brand">{t('roles.system')}</Tag>}
      {shared && <Tag tone="neutral">{t('roles.shared')}</Tag>}
      {/* 平台账号看得到所有家的职位，标上是哪家的；单位账号看到的非共享职位都是自己家的，不标 */}
      {isPlatform && role.org && <Tag tone="neutral">{role.org.name}</Tag>}
    </span>
  );
}

function RoleCounts({ role }: { role: Role }) {
  const { t } = useTranslation();
  return (
    <>
      <span className="tabular-nums">{t('roles.userCount', { count: role._count.users })}</span>
      <span className="tabular-nums">
        {role.isSystem ? t('roles.allPermissions') : t('roles.permissionCount', { count: role.permissions.length })}
      </span>
    </>
  );
}

/**
 * 右边（手机上是底部弹出）：职位头 + 权限表。能改的直接在表上勾、点保存；改不了的只读，写着为什么。
 */
function RoleDetail({
  role,
  onCreated,
  onDeleted,
  onCancelNew,
  bare,
}: {
  role?: Role;
  onCreated: (role: Role) => void;
  onDeleted: () => void;
  onCancelNew: () => void;
  /** 在手机的弹出层里：不再套卡片 */
  bare: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const { role: myRole, orgId: myOrgId, canAny } = usePermissions();
  const [error, setError] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  // 查看成员（CR-075）：列人要调用户列表，门槛和用户页一样（user.manage | site.manage）
  const canListUsers = canAny([Permission.USER_MANAGE, Permission.SITE_MANAGE]);
  const [showMembers, setShowMembers] = useState(false);

  const remove = useMutation({
    mutationFn: () => rolesApi.remove(role!.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      onDeleted();
    },
    onError: (err) => setError(toMessage(err)),
  });

  // 和服务端同一条判断（CR-073）：共享的只有超管，本家的不能比自己大
  const editable = !role || (myRole !== null && canEditRole({ role: myRole, orgId: myOrgId }, role));
  const shared = role && !role.isSystem && role.orgId === null;

  const actions = role && (canListUsers || editable) && (
    <>
      {canListUsers && (
        <Button size="sm" variant="ghost" onClick={() => setShowMembers((v) => !v)}>
          {showMembers ? t('roles.hideMembers') : t('roles.showMembers')}
        </Button>
      )}
      {editable && (
        <Button
          size="sm"
          variant="dangerOutline"
          loading={remove.isPending}
          onClick={() => {
            setError(null);
            if (window.confirm(t('roles.deleteConfirm', { name: role.name }))) remove.mutate();
          }}
        >
          {t('roles.delete')}
        </Button>
      )}
    </>
  );

  const body = (
    <>
      {bare ? (
        // 手机：标题已经在弹出层顶上，这里只剩按钮
        actions && <div className="flex flex-wrap items-center gap-2 border-b border-rule px-4 py-2.5">{actions}</div>
      ) : (
        <CardHeader
          title={role ? <RoleTitle role={role} /> : t('roles.create')}
          description={role && <RoleSummary role={role} />}
          action={actions}
        />
      )}
      {error && (
        <div className="border-b border-rule px-4 py-3">
          <Alert>{error}</Alert>
        </div>
      )}
      {showMembers && role && <RoleMembers roleId={role.id} />}
      {!editable && role && (
        <p className="border-b border-rule px-4 py-2.5 text-body text-ink-2">
          {role.isSystem ? t('roles.systemHint') : shared ? t('roles.sharedHint') : t('roles.readOnlyHint')}
        </p>
      )}
      <RoleForm
        key={formKey}
        role={role}
        readOnly={!editable}
        onSaved={(saved) => {
          if (!role) onCreated(saved);
        }}
        onCancel={() => (role ? setFormKey((k) => k + 1) : onCancelNew())}
      />
    </>
  );

  return bare ? (
    <div className="-mx-5 -my-5 max-md:-mx-4 max-md:-my-4">{body}</div>
  ) : (
    // clip 而不是 hidden：hidden 会让卡片自己成为滚动容器，底下的保存栏就贴不住屏幕底
    <Card className="overflow-clip">{body}</Card>
  );
}

/** 几个人 · 几项权限 · 说明 */
function RoleSummary({ role }: { role: Role }) {
  const { t } = useTranslation();
  return (
    <>
      {[
        t('roles.userCount', { count: role._count.users }),
        role.isSystem ? t('roles.allPermissions') : t('roles.permissionCount', { count: role.permissions.length }),
        role.description,
      ]
        .filter(Boolean)
        .join(' · ')}
    </>
  );
}

/**
 * 挂在这个职位上的人（CR-075，客户原话「再添加一个查看哪些成员的按键」）。
 * 用的是用户列表按 `roleId` 筛 —— 服务端照样按单位圈范围，单位管理员只看到自己家的人，和行上的人数对得上。
 * 列前 200 个（接口上限）；再多的去用户页搜。
 */
function RoleMembers({ roleId }: { roleId: number }) {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();
  const { isPlatform } = usePermissions();
  const members = useQuery({
    // `users` 打头：用户页改了人（换职位、停用），这里跟着失效
    queryKey: ['users', 'by-role', roleId],
    queryFn: () => usersApi.list({ roleId, page: 1, pageSize: 200 }),
  });

  if (members.isLoading) {
    return (
      <div className="flex justify-center border-b border-rule py-4 text-ink-3">
        <Spinner />
      </div>
    );
  }
  if (members.isError) {
    return (
      <div className="border-b border-rule px-4 py-3">
        <Alert>{toMessage(members.error)}</Alert>
      </div>
    );
  }
  const data = members.data;
  if (!data || data.items.length === 0) {
    return <p className="border-b border-rule px-4 py-3 text-body text-ink-3">{t('roles.membersEmpty')}</p>;
  }

  return (
    <div className="border-b border-rule bg-surface-2/40 px-4 py-3">
      <p className="mb-1.5 text-caption text-ink-3">{t('roles.membersCount', { count: data.total })}</p>
      {/* 限高、超出在框里滚（客户补充：「查看成员这个表要注意限制行数，超过了可以滑动」）：
          几十个人全摊开，下面的权限表就被推到好几屏之外了。大约露 8 行 */}
      <ul className="flex max-h-72 flex-col divide-y divide-rule overflow-y-auto overscroll-contain rounded-lg border border-rule bg-surface">
        {data.items.map((user) => (
          <li key={user.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-body">
            <span className="w-10 shrink-0 text-caption tabular-nums text-ink-3">{user.employeeNo}</span>
            <span className="font-medium text-ink">{user.name}</span>
            {isPlatform && <span className="text-ink-3">{user.org?.name ?? t('org.platform')}</span>}
            {user.status !== UserStatus.ACTIVE && <Badge tone="danger">{t('user.statusSuspended')}</Badge>}
          </li>
        ))}
      </ul>
      {data.total > data.items.length && (
        <p className="mt-2 text-caption text-ink-3">{t('roles.membersMore', { count: data.total })}</p>
      )}
    </div>
  );
}

/**
 * 名字、说明 + 权限表（CR-082：按模块一行，一行一组勾，每个勾下面一句它意味着什么）。
 * 建职位和改职位是同一张表；动不了的职位传 `readOnly`，勾全部灰掉、没有保存。
 *
 * 提交的是整份勾选结果，不是增量 —— 服务端按清单校验每一项，
 * 勾了清单里没有的会回 PERMISSION_INVALID（只可能是前后端版本不一致）。
 *
 * 超管以外的人（CR-073）：自己没有的权限、管人的两项勾不了，勾选框灰掉并说明为什么 ——
 * 服务端按同一条子集规则拒绝，界面上就不该让人勾完了点保存才报错。
 */
function RoleForm({
  role,
  readOnly,
  onSaved,
  onCancel,
}: {
  role?: Role;
  readOnly: boolean;
  onSaved: (role: Role) => void;
  /** 改的时候是「撤销修改」，建的时候是「取消」 */
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const { role: myRole, isSystem, isPlatform } = usePermissions();
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  // 归哪家：只有平台账号建职位时选（CR-073）；单位账号建的永远是自己家的，改的时候不能换
  const [orgId, setOrgId] = useState<OrgChoice>('');
  const pickOrg = !role && isPlatform;
  /**
   * 勾选结果每次都过一遍 normalizePermissions（CR-040）：勾「审批采购」自动带上「查看采购」，
   * 被带上的那个锁住、下面写着是谁带上的。服务端保存时也会补齐，这里只是让人看得见。
   * 超管职位显示全勾（客户原话「超级管理员权限无法被修改」）。
   */
  const initial = useMemo(
    () => normalizePermissions(role?.isSystem ? ALL_PERMISSIONS : (role?.permissions ?? [])),
    [role],
  );
  const [granted, setGranted] = useState<Set<PermissionKey>>(() => new Set(initial));

  // 改过没有：没改的时候保存是灰的，「撤销修改」也没东西可撤。列表刷新后 role 换成新的一份，跟着归零
  const dirty =
    !role ||
    name !== role.name ||
    description !== (role.description ?? '') ||
    granted.size !== initial.length ||
    initial.some((k) => !granted.has(k));

  /** 超管以外的人勾不了的：自己没有的（子集规则）、管人的两项（建不出管理员）。超管什么都能勾 */
  const outOfReach = (key: PermissionKey): string | null => {
    if (isSystem) return null;
    if ((ADMIN_PERMISSIONS as readonly string[]).includes(key)) return t('roles.permAdminOnly');
    if (!hasPermission(myRole, key)) return t('roles.permOutOfReach');
    return null;
  };

  const toggle = (key: PermissionKey, on: boolean) =>
    setGranted((cur) => {
      const next = new Set(cur);
      if (on) next.add(key);
      else next.delete(key);
      return new Set(normalizePermissions([...next]));
    });

  const toggleGroup = (keys: readonly PermissionKey[], on: boolean) =>
    setGranted((cur) => {
      const next = new Set(cur);
      for (const key of keys) {
        if (outOfReach(key)) continue;
        if (on) next.add(key);
        else next.delete(key);
      }
      return new Set(normalizePermissions([...next]));
    });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        description: description.trim(),
        permissions: [...granted],
      };
      if (role) return rolesApi.update(role.id, body);
      return rolesApi.create({ ...body, ...(pickOrg ? { orgId: orgId as number | null } : {}) });
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      // 挂在这个职位上的人权限变了，用户列表里的职位摘要也要跟着刷
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      onSaved(saved);
    },
    onError: (err) => setError(toMessage(err)),
  });

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setError(null);
        if (pickOrg && orgId === '') {
          setError(t('errors.ORG_REQUIRED'));
          return;
        }
        save.mutate();
      }}
    >
      {!readOnly && (
        <div className="flex flex-col gap-4 border-b border-rule px-4 py-4">
          {error && <Alert>{error}</Alert>}
          {pickOrg && (
            <OrgSelect
              id="role-org"
              label={t('roles.orgLabel')}
              value={orgId}
              onChange={setOrgId}
              // 共享职位只有超管能建（它会出现在每一家的下拉里）
              platformOption={isSystem ? t('roles.orgShared') : undefined}
              hint={t('roles.orgHint')}
            />
          )}
          <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <Field label={t('roles.name')} htmlFor="role-name" required hint={t('roles.nameHint')}>
              <Input
                id="role-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={32}
                autoFocus={!role}
              />
            </Field>
            <Field label={t('roles.descriptionLabel')} htmlFor="role-desc" hint={t('roles.descriptionHint')}>
              <Input
                id="role-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={200}
              />
            </Field>
          </div>
        </div>
      )}

      <p className="border-b border-rule bg-surface-2/40 px-4 py-2.5 text-caption leading-relaxed text-ink-3">
        {t('roles.permissionsHint')}
      </p>

      {/* 权限表：左边模块名（+ 全选），右边这一组的勾 */}
      <div>
        {PERMISSION_GROUPS.map((group) => {
          const reachable = group.permissions.filter((k) => !outOfReach(k));
          const all = reachable.length > 0 && reachable.every((k) => granted.has(k));
          return (
            <div
              key={group.key}
              className="grid gap-x-4 border-b border-rule px-4 py-2.5 last:border-0 md:grid-cols-[7.5rem_minmax(0,1fr)]"
            >
              <div className="flex items-center justify-between gap-2 md:flex-col md:items-start md:justify-start md:pt-2">
                <span className="text-body font-semibold text-ink">
                  {t(PERMISSION_GROUP_LABEL_KEY[group.key] ?? '')}
                </span>
                {!readOnly && reachable.length > 1 && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.permissions, !all)}
                    className="min-h-touch-sm text-caption text-brand-600 hover:underline"
                  >
                    {all ? t('roles.clearGroup') : t('roles.fillGroup')}
                  </button>
                )}
              </div>
              <div className="grid gap-x-3 sm:grid-cols-2 2xl:grid-cols-3">
                {group.permissions.map((key) => {
                  const lockedBy = impliedBy(key, [...granted]);
                  const blocked = readOnly ? null : outOfReach(key);
                  return (
                    <Checkbox
                      key={key}
                      label={t(PERMISSION_LABEL_KEY[key])}
                      hint={
                        blocked ??
                        (lockedBy.length > 0 && !readOnly
                          ? t('roles.impliedBy', { name: t(PERMISSION_LABEL_KEY[lockedBy[0]!]) })
                          : t(PERMISSION_HINT_KEY[key]))
                      }
                      checked={granted.has(key)}
                      disabled={readOnly || lockedBy.length > 0 || blocked !== null}
                      onChange={(e) => toggle(key, e.target.checked)}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {!readOnly && (
        // 贴在屏幕底：权限表有两屏长，改完不用滚到底找保存
        <div className="sticky-bar flex flex-wrap items-center justify-end gap-2 border-t border-rule bg-surface px-4 py-3">
          {role && role._count.users > 0 && dirty && (
            <p className="mr-auto min-w-0 flex-1 basis-60 text-caption leading-relaxed text-ink-3">
              {t('roles.editAffects', { count: role._count.users })}
            </p>
          )}
          <Button type="button" variant="secondary" onClick={onCancel} disabled={role !== undefined && !dirty}>
            {role ? t('app.revert') : t('app.cancel')}
          </Button>
          <Button type="submit" loading={save.isPending} disabled={!dirty}>
            {role ? t('app.save') : t('roles.create')}
          </Button>
        </div>
      )}
    </form>
  );
}
